import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import { useClickRef, ThemeModeType } from '@make-software/csprclick-ui';
import { AccountType } from '@make-software/csprclick-core-types';

import { AppTheme } from '@/theme';
import { ClickTopBar, PageFooter, NavigationMenu } from '@/components';

import { ConnectedAccountContext } from '@/contexts';

import HomeScene from '@/scenes/home';
import InboxScene from '@/scenes/inbox';
import OutboxScene from '@/scenes/outbox';
import StampsScene from '@/scenes/stamps';
import PrivacyPolicyScene from '@/scenes/privacy-policy';
import TermsOfServiceScene from '@/scenes/terms-of-service';

import {
  HOME_PATH,
  INBOX_PATH,
  OUTBOX_PATH,
  STAMPS_PATH,
  PRIVACY_POLICY_PATH,
  TERMS_OF_SERVICE_PATH
} from '@/paths';
import { useGetStampBalanceForAccount } from '@/hooks';

const AppRoutes = () => {
  const { hash } = useLocation();

  if (hash.startsWith('#' + STAMPS_PATH)) return <StampsScene />;
  if (hash.startsWith('#' + OUTBOX_PATH)) return <OutboxScene />;
  if (hash.startsWith('#' + INBOX_PATH)) return <InboxScene />;
  if (hash.startsWith('#' + PRIVACY_POLICY_PATH)) return <PrivacyPolicyScene />;
  if (hash.startsWith('#' + TERMS_OF_SERVICE_PATH)) return <TermsOfServiceScene />;

  return (
    <Routes>
      <Route path={HOME_PATH} element={<HomeScene />} />
      <Route path={INBOX_PATH} element={<InboxScene />} />
      <Route path={OUTBOX_PATH} element={<OutboxScene />} />
      <Route path={STAMPS_PATH} element={<StampsScene />} />
      <Route path={PRIVACY_POLICY_PATH} element={<PrivacyPolicyScene />} />
      <Route path={TERMS_OF_SERVICE_PATH} element={<TermsOfServiceScene />} />
    </Routes>
  );
};

const App = () => {
  const clickRef = useClickRef();
  const [themeMode, setThemeMode] = useState<ThemeModeType>(ThemeModeType.light);
  const [connectedAccount, setConnectedAccount] = useState<AccountType | null>(null);

  const {
    response: stampBalance,
    refetch: refreshStampBalance
  } = useGetStampBalanceForAccount(connectedAccount?.public_key);

  useEffect(() => {
    if (!clickRef) return;

    const handleSignedIn = (evt: any) => setConnectedAccount(evt.account);
    const handleSwitchedAccount = (evt: any) => setConnectedAccount(evt.account);
    const handleSignedOut = () => setConnectedAccount(null);

    clickRef.on('csprclick:signed_in', handleSignedIn);
    clickRef.on('csprclick:switched_account', handleSwitchedAccount);
    clickRef.on('csprclick:signed_out', handleSignedOut);

    return () => {
      clickRef.off('csprclick:signed_in', handleSignedIn);
      clickRef.off('csprclick:switched_account', handleSwitchedAccount);
      clickRef.off('csprclick:signed_out', handleSignedOut);
    };
  }, [clickRef]);

  return (
    <BrowserRouter>
      <ThemeProvider theme={AppTheme[themeMode]}>
        <ConnectedAccountContext.Provider value={{ connectedAccount, stampBalance, refreshStampBalance }}>
          <ClickTopBar
            themeMode={themeMode}
            onThemeSwitch={() =>
              setThemeMode(
                themeMode === ThemeModeType.light ? ThemeModeType.dark : ThemeModeType.light
              )
            }
          />
          <NavigationMenu />

          <AppRoutes />
        </ConnectedAccountContext.Provider>
        <PageFooter />
      </ThemeProvider>
    </BrowserRouter>
  );
};

export default App;
